export const PLANET_HD_ACCESS_STATES = {
  UNKNOWN: 'unknown',
  PREFLIGHT: 'preflight',
  CONFIRMING: 'confirming',
  PROVISIONING: 'provisioning',
  READY: 'ready',
  EXPIRED: 'expired',
  STALE: 'stale',
  SUSPENDED: 'suspended',
  UNAVAILABLE: 'unavailable',
  FAILED: 'failed',
}

const STATUS_ALIASES = {
  active: PLANET_HD_ACCESS_STATES.READY,
  enabled: PLANET_HD_ACCESS_STATES.READY,
  subscribed: PLANET_HD_ACCESS_STATES.READY,
  ready: PLANET_HD_ACCESS_STATES.READY,
  preflight: PLANET_HD_ACCESS_STATES.PREFLIGHT,
  not_enabled: PLANET_HD_ACCESS_STATES.PREFLIGHT,
  reserved: PLANET_HD_ACCESS_STATES.PREFLIGHT,
  awaiting_confirmation: PLANET_HD_ACCESS_STATES.PREFLIGHT,
  confirming: PLANET_HD_ACCESS_STATES.CONFIRMING,
  subscribing: PLANET_HD_ACCESS_STATES.PROVISIONING,
  provisioning: PLANET_HD_ACCESS_STATES.PROVISIONING,
  backfilling: PLANET_HD_ACCESS_STATES.PROVISIONING,
  preparing: PLANET_HD_ACCESS_STATES.PROVISIONING,
  expired: PLANET_HD_ACCESS_STATES.EXPIRED,
  stale: PLANET_HD_ACCESS_STATES.STALE,
  geometry_stale: PLANET_HD_ACCESS_STATES.STALE,
  stale_geometry: PLANET_HD_ACCESS_STATES.STALE,
  geometry_changed: PLANET_HD_ACCESS_STATES.STALE,
  suspended: PLANET_HD_ACCESS_STATES.SUSPENDED,
  suspended_pu: PLANET_HD_ACCESS_STATES.SUSPENDED,
  disabled: PLANET_HD_ACCESS_STATES.SUSPENDED,
  unavailable: PLANET_HD_ACCESS_STATES.UNAVAILABLE,
  failed: PLANET_HD_ACCESS_STATES.FAILED,
  error: PLANET_HD_ACCESS_STATES.FAILED,
}

const numberOrZero = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getProviderPeriodEnd = (access) =>
  access?.provider_period?.period_end || access?.provider_period?.end

export const getPlanetHdCompanyPeriodEnd = (access) =>
  access?.company_period?.period_end ||
  access?.period_end ||
  access?.hd_period_end ||
  (!access?.company_period ? getProviderPeriodEnd(access) : null)

const hasExpiredPeriod = (rawPeriodEnd, now) => {
  if (!rawPeriodEnd) {
    return false
  }

  const periodEnd = new Date(rawPeriodEnd)
  return !Number.isNaN(periodEnd.getTime()) && periodEnd <= now
}

export const getPlanetHdRequiredHa = (access) =>
  Math.max(
    0,
    numberOrZero(
      access?.required_ha ??
      access?.company_required_ha ??
      access?.required_hd_ha ??
      access?.new_area_ha ??
      access?.delta_ha
    )
  )

export const getPlanetHdAccessState = (access, now = new Date()) => {
  if (!access) {
    return PLANET_HD_ACCESS_STATES.UNKNOWN
  }

  if (access.suspended === true || access.is_suspended === true) {
    return PLANET_HD_ACCESS_STATES.SUSPENDED
  }

  if (access.geometry_stale === true || access.stale === true) {
    return PLANET_HD_ACCESS_STATES.STALE
  }

  if (
    access.company_period?.status === 'expired' ||
    hasExpiredPeriod(getPlanetHdCompanyPeriodEnd(access), now)
  ) {
    return PLANET_HD_ACCESS_STATES.EXPIRED
  }

  if (
    access.operational_access_available === false ||
    access.supplier?.pool_available === false ||
    hasExpiredPeriod(getProviderPeriodEnd(access), now) ||
    access.provider_period?.status === 'expired'
  ) {
    return PLANET_HD_ACCESS_STATES.UNAVAILABLE
  }

  if (
    access.company_period?.period_id && access.company_period_id &&
    access.company_period.period_id !== access.company_period_id
  ) {
    return access.can_confirm === true
      ? PLANET_HD_ACCESS_STATES.PREFLIGHT
      : PLANET_HD_ACCESS_STATES.UNAVAILABLE
  }

  const explicitStatus = String(
    access.state || access.status || access.subscription_status || ''
  ).toLowerCase()
  if (STATUS_ALIASES[explicitStatus]) {
    if (
      STATUS_ALIASES[explicitStatus] === PLANET_HD_ACCESS_STATES.PREFLIGHT &&
      access.can_confirm === false
    ) {
      return PLANET_HD_ACCESS_STATES.UNAVAILABLE
    }
    return STATUS_ALIASES[explicitStatus]
  }

  const backendActive = access.active ?? access.is_active ?? access.enabled
  if (backendActive === true && getPlanetHdRequiredHa(access) <= 0.01) {
    return PLANET_HD_ACCESS_STATES.READY
  }

  if (access.can_confirm === true || getPlanetHdRequiredHa(access) > 0.01) {
    return PLANET_HD_ACCESS_STATES.PREFLIGHT
  }

  if (access.can_confirm === false || backendActive === false) {
    return PLANET_HD_ACCESS_STATES.UNAVAILABLE
  }

  return PLANET_HD_ACCESS_STATES.UNKNOWN
}

export const canConfirmPlanetHdAccess = (access, now = new Date()) =>
  [
    PLANET_HD_ACCESS_STATES.PREFLIGHT,
    PLANET_HD_ACCESS_STATES.STALE,
  ].includes(getPlanetHdAccessState(access, now)) &&
  access?.can_confirm !== false

export const canUsePlanetHd = (access, now = new Date()) =>
  getPlanetHdAccessState(access, now) === PLANET_HD_ACCESS_STATES.READY

export const isPlanetHdProvisioning = (access, now = new Date()) =>
  getPlanetHdAccessState(access, now) === PLANET_HD_ACCESS_STATES.PROVISIONING
